import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  Bell,
  Menu,
  ChevronDown,
  RefreshCw,
  Activity,
  DollarSign,
  CreditCard,
  LogOut,
  User,
  Shield,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  PieChart,
  Clock,
  Database,
  Crown,
  FileCheck, // Added for Legal Documents
  Users as UsersIcon, // Added for Demo Requests
} from 'lucide-react';

// Import components
import PricingManagement from './PricingManagement';
import PaymentManagement from './PaymentManagement';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState('dashboard');
  const [stats, setStats] = useState({
    applications: { total: 0, pending: 0, approved: 0, rejected: 0, review: 0 },
    tenants: { total: 0, active: 0, suspended: 0 },
    users: { total: 0, admins: 0, students: 0, faculty: 0 },
    payments: { total: 0, revenue: 0, credits: 0, refunds: 0 },
    subscriptions: { total: 0, active: 0, trial: 0, expired: 0 },
    recentActivities: [],
    trends: {
      applications: { value: 0, direction: 'up' },
      tenants: { value: 0, direction: 'up' },
      users: { value: 0, direction: 'up' },
      revenue: { value: 0, direction: 'up' },
      subscriptions: { value: 0, direction: 'up' }
    }
  });

  // Legal Documents Stats
  const [legalDocsStats, setLegalDocsStats] = useState({
    total: 0,
    draft: 0,
    published: 0,
    archived: 0,
    pendingAcceptance: 0
  });

  // Demo Requests Stats
  const [demoRequestsStats, setDemoRequestsStats] = useState({
    total: 0,
    pending: 0,
    reviewed: 0,
    scheduled: 0,
    completed: 0,
    cancelled: 0
  });

  // ============================================
  // PRICING STATE
  // ============================================
  const [pricingData, setPricingData] = useState({
    emailPricePerCredit: 0.20,
    smsPricePerCredit: 0.15,
    currency: 'INR',
    currencySymbol: '₹',
    currencyCode: 'INR',
    taxPercentage: 0,
    taxDescription: '',
    bulkDiscountTiers: []
  });

  const [tenantPricingData, setTenantPricingData] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);

  // ============================================
  // AUTH CHECK
  // ============================================
  useEffect(() => {
    checkAuth();
    loadDashboardData();
    loadLegalDocsStats();
    loadDemoRequestsStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/signin');
        return;
      }
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(userInfo);
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/signin');
    }
  };

  // ============================================
  // LOAD LEGAL DOCS STATS
  // ============================================
  const loadLegalDocsStats = async () => {
    try {
      // You can replace this with actual API call
      // const response = await api.get('/api/super-admin/legal-documents');
      // const docs = response.data.data;
      
      // Simulated data for now
      setLegalDocsStats({
        total: 8,
        draft: 3,
        published: 4,
        archived: 1,
        pendingAcceptance: 2
      });
    } catch (error) {
      console.error('Failed to load legal docs stats:', error);
    }
  };

  // ============================================
  // LOAD DEMO REQUESTS STATS
  // ============================================
  const loadDemoRequestsStats = async () => {
    try {
      // You can replace this with actual API call
      // const response = await api.get('/api/super-admin/demo-requests?limit=1');
      // setDemoRequestsStats(response.data.data.counts);
      
      // Simulated data for now
      setDemoRequestsStats({
        total: 24,
        pending: 8,
        reviewed: 5,
        scheduled: 6,
        completed: 3,
        cancelled: 2
      });
    } catch (error) {
      console.error('Failed to load demo requests stats:', error);
    }
  };

  // ============================================
  // LOAD DASHBOARD DATA
  // ============================================
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Simulated API response - replace with actual API calls
      setTimeout(() => {
        setStats({
          applications: {
            total: 245,
            pending: 45,
            approved: 156,
            rejected: 32,
            review: 12
          },
          tenants: {
            total: 48,
            active: 42,
            suspended: 6
          },
          users: {
            total: 15230,
            admins: 48,
            students: 14850,
            faculty: 332
          },
          payments: {
            total: 1250,
            revenue: 456789.50,
            credits: 2500000,
            refunds: 12345.00
          },
          subscriptions: {
            total: 48,
            active: 38,
            trial: 4,
            expired: 6
          },
          recentActivities: [
            { id: 1, type: 'payment', action: 'New payment received', user: 'Sunrise College', time: '5 min ago', status: 'completed', amount: '₹2,500' },
            { id: 2, type: 'application', action: 'New application submitted', user: 'Greenwood School', time: '1 hour ago', status: 'pending' },
            { id: 3, type: 'refund', action: 'Refund processed', user: 'Blue Valley Institute', time: '3 hours ago', status: 'completed', amount: '₹500' },
            { id: 4, type: 'tenant', action: 'New tenant created', user: 'Riverside Academy', time: '5 hours ago', status: 'active' },
            { id: 5, type: 'pricing', action: 'Pricing updated', user: 'Super Admin', time: '1 day ago', status: 'completed' }
          ],
          trends: {
            applications: { value: 12, direction: 'up' },
            tenants: { value: 8, direction: 'up' },
            users: { value: 5, direction: 'up' },
            revenue: { value: 15, direction: 'up' },
            subscriptions: { value: 3, direction: 'up' }
          }
        });
        setLoading(false);
      }, 800);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setLoading(false);
    }
  };

  // ============================================
  // REFRESH DATA
  // ============================================
  const refreshData = async () => {
    setRefreshing(true);
    await loadDashboardData();
    await loadLegalDocsStats();
    await loadDemoRequestsStats();
    setRefreshing(false);
  };

  // ============================================
  // FETCH PRICING DATA
  // ============================================
  const fetchPricingData = async () => {
    setPricingLoading(true);
    try {
      // Simulated API calls - replace with actual API
      setPricingData({
        emailPricePerCredit: 0.20,
        smsPricePerCredit: 0.15,
        currency: 'INR',
        currencySymbol: '₹',
        currencyCode: 'INR',
        taxPercentage: 18,
        taxDescription: 'GST',
        bulkDiscountTiers: [
          { minCredits: 10000, discountPercentage: 10, communicationType: 'both' },
          { minCredits: 50000, discountPercentage: 20, communicationType: 'email' }
        ]
      });
      
      setTenantPricingData([
        { tenantId: 'TYN94781358', organizationName: 'Pramod University', pricing: { email: { basePrice: 0.20, effectivePrice: 0.16, discountPercentage: 20 }, sms: { basePrice: 0.15, effectivePrice: 0.15, discountPercentage: 0 } } },
        { tenantId: 'TYN31126910', organizationName: 'Pragathi Pre-University College', pricing: { email: { basePrice: 0.20, effectivePrice: 0.20, discountPercentage: 0 }, sms: { basePrice: 0.15, effectivePrice: 0.135, discountPercentage: 10 } } }
      ]);
      
      setAuditLogs([
        { _id: '1', action: 'price_updated', performedBy: 'admin@truyon.com', targetTenantId: null, details: { oldVersion: 1, newVersion: 2 }, createdAt: new Date().toISOString() },
        { _id: '2', action: 'discount_applied', performedBy: 'admin@truyon.com', targetTenantId: 'TYN94781358', details: { discountValue: 20, communicationType: 'email' }, createdAt: new Date().toISOString() }
      ]);
    } catch (error) {
      console.error('❌ Error fetching pricing data:', error);
    } finally {
      setPricingLoading(false);
    }
  };

  // ============================================
  // NAVIGATION HANDLERS
  // ============================================
  const navigateToDashboard = () => setActivePage('dashboard');
  const navigateToBackup = () => navigate('/super-admin/backup');
  const navigateToPricing = () => {
    setActivePage('pricing');
    fetchPricingData();
  };
  const navigateToPayments = () => setActivePage('payments');
  const navigateToSubscription = () => navigate('/super-admin/subscription');
  const navigateToLegalDocuments = () => {
    navigate('/super-admin/legal-documents');
  };
  const navigateToDemoRequests = () => {
    navigate('/super-admin/demo-requests');
  };

  // ============================================
  // NAVIGATION ITEMS
  // ============================================
  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', onClick: navigateToDashboard },
    { id: 'backup', icon: Database, label: 'Backup', onClick: navigateToBackup },
    { id: 'payments', icon: CreditCard, label: 'Payments', badge: stats.payments.total, onClick: navigateToPayments },
    { id: 'pricing', icon: DollarSign, label: 'Pricing', onClick: navigateToPricing },
    { id: 'subscription', icon: Crown, label: 'Subscription', badge: stats.subscriptions.active, onClick: navigateToSubscription },
    { id: 'demo-requests', icon: UsersIcon, label: 'Demo Requests', badge: demoRequestsStats.pending, onClick: navigateToDemoRequests },
    { id: 'legal', icon: FileCheck, label: 'Legal Docs', badge: legalDocsStats.pendingAcceptance, onClick: navigateToLegalDocuments },
    { id: 'applications', icon: FileText, label: 'Applications', badge: stats.applications.pending + stats.applications.review, onClick: () => navigate('/super-admin/applications') },
    { id: 'tenants', icon: Building2, label: 'Tenants', badge: stats.tenants.total, onClick: () => navigate('/super-admin/tenants') },
    { id: 'users', icon: Users, label: 'Users', badge: stats.users.total, onClick: () => navigate('/super-admin/users') },
  ];

  // ============================================
  // RENDER HELPERS
  // ============================================
  const StatCard = ({ title, value, icon: Icon, trend, color, subtitle, onClick }) => (
    <div 
      onClick={onClick}
      className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-xl transition-all duration-300 cursor-pointer group hover:border-indigo-300"
    >
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl bg-${color}-50 group-hover:bg-${color}-100 transition-colors`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${
            trend.direction === 'up' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
          }`}>
            {trend.direction === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            <span>{trend.value}%</span>
          </div>
        )}
      </div>
      <h3 className="text-2xl font-bold text-slate-900 mt-3">{value.toLocaleString()}</h3>
      <p className="text-sm text-slate-500 mt-0.5">{title}</p>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );

  const ActivityIcon = ({ type }) => {
    const icons = {
      payment: <CreditCard className="w-4 h-4 text-emerald-500" />,
      application: <FileText className="w-4 h-4 text-blue-500" />,
      refund: <ArrowUpRight className="w-4 h-4 text-amber-500" />,
      tenant: <Building2 className="w-4 h-4 text-purple-500" />,
      pricing: <DollarSign className="w-4 h-4 text-indigo-500" />,
      user: <Users className="w-4 h-4 text-cyan-500" />,
      demo: <UsersIcon className="w-4 h-4 text-orange-500" />
    };
    return icons[type] || <Activity className="w-4 h-4 text-slate-500" />;
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      rejected: 'bg-rose-50 text-rose-700 border-rose-200',
      review: 'bg-orange-50 text-orange-700 border-orange-200',
      active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      suspended: 'bg-rose-50 text-rose-700 border-rose-200',
      completed: 'bg-blue-50 text-blue-700 border-blue-200',
      scheduled: 'bg-green-50 text-green-700 border-green-200',
      reviewed: 'bg-purple-50 text-purple-700 border-purple-200',
      cancelled: 'bg-red-50 text-red-700 border-red-200'
    };
    
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // ============================================
  // SIDEBAR COMPONENT
  // ============================================
  const Sidebar = () => (
    <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-slate-200 transition-all duration-300 flex flex-col flex-shrink-0`}>
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-slate-200">
        <div className={`flex items-center ${sidebarCollapsed ? 'justify-center w-full' : 'gap-3'}`}>
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <span className="text-white font-bold text-lg">T</span>
          </div>
          {!sidebarCollapsed && (
            <>
              <span className="font-semibold text-slate-900 text-base">Truyon</span>
              <span className="text-xs text-slate-400">v2.0</span>
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 overflow-y-auto">
        <div className="space-y-1 px-3">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-xl transition-all duration-200 relative ${
                activePage === item.id 
                  ? 'bg-indigo-50 text-indigo-600 shadow-sm' 
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <item.icon className={`w-5 h-5 flex-shrink-0 ${activePage === item.id ? 'text-indigo-600' : 'text-slate-500'}`} />
              {!sidebarCollapsed && (
                <>
                  <span className="text-sm font-medium flex-1 text-left">{item.label}</span>
                  {item.badge && (
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      activePage === item.id 
                        ? 'bg-indigo-100 text-indigo-600' 
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </>
              )}
              {sidebarCollapsed && item.badge && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-indigo-600 rounded-full"></span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {user?.name?.charAt(0) || 'A'}
          </div>
          {!sidebarCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name || 'Admin User'}</p>
              <p className="text-xs text-slate-500 truncate">Super Administrator</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );

  // ============================================
  // HEADER COMPONENT
  // ============================================
  const Header = ({ title }) => (
    <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-6 sticky top-0 z-30">
      <div className="flex items-center gap-4 min-w-0">
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-2 hover:bg-slate-100 rounded-xl transition-colors flex-shrink-0"
        >
          <Menu className="w-5 h-5 text-slate-500" />
        </button>
        <h1 className="text-lg font-semibold text-slate-900 truncate">{title}</h1>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <button
          onClick={refreshData}
          disabled={refreshing}
          className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
        >
          <RefreshCw className={`w-5 h-5 text-slate-500 ${refreshing && 'animate-spin'}`} />
        </button>

        <div className="relative">
          <button
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors relative"
          >
            <Bell className="w-5 h-5 text-slate-500" />
            <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full"></span>
          </button>

          {notificationsOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-slate-200 py-2 z-50">
              <div className="px-4 py-2 border-b border-slate-100">
                <h3 className="font-semibold text-slate-900">Notifications</h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                    <p className="text-sm text-slate-900">New application submitted</p>
                    <p className="text-xs text-slate-500 mt-1">5 minutes ago</p>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2 border-t border-slate-100">
                <button className="text-sm text-indigo-600 hover:text-indigo-700">View all</button>
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
            className="flex items-center gap-2 p-1.5 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl flex items-center justify-center text-white font-medium text-sm">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <ChevronDown className="w-4 h-4 text-slate-500" />
          </button>

          {profileMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-slate-200 py-1 z-50">
              <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 rounded-t-2xl">
                <User className="w-4 h-4" /> Profile
              </button>
              <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Security
              </button>
              <hr className="my-1 border-slate-200" />
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  navigate('/signin');
                }}
                className="w-full text-left px-4 py-2.5 text-sm text-rose-600 hover:bg-slate-50 flex items-center gap-2 rounded-b-2xl"
              >
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );

  // ============================================
  // LEGAL DOCUMENTS CARD COMPONENT
  // ============================================
  const LegalDocumentsCard = () => (
    <div 
      onClick={navigateToLegalDocuments}
      className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-200 p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group hover:border-indigo-400"
    >
      <div className="flex items-start justify-between">
        <div className="p-3 bg-indigo-100 rounded-xl group-hover:bg-indigo-200 transition-colors">
          <FileCheck className="w-6 h-6 text-indigo-600" />
        </div>
        <div className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-600">
          <span>{legalDocsStats.pendingAcceptance} pending</span>
        </div>
      </div>
      <h3 className="text-2xl font-bold text-slate-900 mt-3">{legalDocsStats.total}</h3>
      <p className="text-sm text-slate-600 mt-0.5">Legal Documents</p>
      <div className="flex gap-4 mt-3 text-xs">
        <span className="text-amber-600">📄 {legalDocsStats.draft} Drafts</span>
        <span className="text-emerald-600">✅ {legalDocsStats.published} Published</span>
        <span className="text-gray-500">📦 {legalDocsStats.archived} Archived</span>
      </div>
      <div className="mt-3 text-xs text-indigo-600 font-medium group-hover:underline">
        Manage documents →
      </div>
    </div>
  );

  // ============================================
  // DEMO REQUESTS CARD COMPONENT
  // ============================================
  const DemoRequestsCard = () => (
    <div 
      onClick={navigateToDemoRequests}
      className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border border-orange-200 p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group hover:border-orange-400"
    >
      <div className="flex items-start justify-between">
        <div className="p-3 bg-orange-100 rounded-xl group-hover:bg-orange-200 transition-colors">
          <UsersIcon className="w-6 h-6 text-orange-600" />
        </div>
        <div className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-orange-100 text-orange-600">
          <span>{demoRequestsStats.pending} pending</span>
        </div>
      </div>
      <h3 className="text-2xl font-bold text-slate-900 mt-3">{demoRequestsStats.total}</h3>
      <p className="text-sm text-slate-600 mt-0.5">Demo Requests</p>
      <div className="flex flex-wrap gap-3 mt-3 text-xs">
        <span className="text-amber-600">⏳ {demoRequestsStats.pending} Pending</span>
        <span className="text-blue-600">👁️ {demoRequestsStats.reviewed} Reviewed</span>
        <span className="text-emerald-600">✅ {demoRequestsStats.scheduled} Scheduled</span>
        <span className="text-purple-600">✔️ {demoRequestsStats.completed} Completed</span>
      </div>
      <div className="mt-3 text-xs text-orange-600 font-medium group-hover:underline">
        Manage demo requests →
      </div>
    </div>
  );

  // ============================================
  // DASHBOARD CONTENT
  // ============================================
  const DashboardContent = () => (
    <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
      {/* Welcome Section */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Welcome back, {user?.name || 'Admin'} 👋</h1>
        <p className="text-sm text-slate-500 mt-1">Here's what's happening with your platform today.</p>
      </div>

      {/* Stats Grid - Modern Card Design */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Total Applications"
          value={stats.applications.total}
          icon={FileText}
          trend={stats.trends.applications}
          color="blue"
          onClick={() => navigate('/super-admin/applications')}
        />
        <StatCard
          title="Active Tenants"
          value={stats.tenants.active}
          icon={Building2}
          trend={stats.trends.tenants}
          color="purple"
          onClick={() => navigate('/super-admin/tenants')}
        />
        <StatCard
          title="Total Users"
          value={stats.users.total}
          icon={Users}
          trend={stats.trends.users}
          color="emerald"
          onClick={() => navigate('/super-admin/users')}
        />
        <StatCard
          title="Revenue"
          value={`₹${(stats.payments.revenue).toFixed(2)}`}
          icon={DollarSign}
          trend={stats.trends.revenue}
          color="indigo"
          subtitle={`${stats.payments.total} payments • ${stats.payments.credits.toLocaleString()} credits`}
          onClick={navigateToPayments}
        />
      </div>

      {/* Quick Actions - Modern Cards with Legal Documents and Demo Requests Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-4 mb-6">
        <QuickActionCard
          icon={Database}
          title="Backup"
          description="Manage database backups"
          color="indigo"
          onClick={navigateToBackup}
        />
        <QuickActionCard
          icon={CreditCard}
          title="Payments"
          description="View all payments"
          color="emerald"
          onClick={navigateToPayments}
        />
        <QuickActionCard
          icon={DollarSign}
          title="Pricing"
          description="Configure pricing"
          color="indigo"
          onClick={navigateToPricing}
        />
        <QuickActionCard
          icon={Crown}
          title="Subscription"
          description="Manage plans & tenants"
          color="amber"
          onClick={navigateToSubscription}
        />
        <QuickActionCard
          icon={FileText}
          title="Applications"
          description={`${stats.applications.pending} pending`}
          color="blue"
          onClick={() => navigate('/super-admin/applications')}
        />
        <DemoRequestsCard />
        <LegalDocumentsCard />
      </div>

      {/* Recent Activity - Modern Card */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-400" />
            <h2 className="text-base font-semibold text-slate-900">Recent Activity</h2>
          </div>
          <button className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            View all
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {stats.recentActivities.slice(0, 5).map((activity) => (
            <div key={activity.id} className="px-6 py-3.5 flex items-center gap-3 hover:bg-slate-50 transition-colors">
              <div className="p-2 bg-slate-100 rounded-xl flex-shrink-0">
                <ActivityIcon type={activity.type} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-900 font-medium truncate">{activity.action}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-500 truncate">{activity.user}</span>
                  <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                  <span className="text-xs text-slate-400">{activity.time}</span>
                  {activity.amount && (
                    <>
                      <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                      <span className="text-xs font-medium text-emerald-600">{activity.amount}</span>
                    </>
                  )}
                </div>
              </div>
              <StatusBadge status={activity.status} />
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Section - Analytics Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Payment Overview</h3>
            <PieChart className="w-5 h-5 text-slate-400" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Total Revenue</span>
              <span className="text-sm font-bold text-slate-900">₹{stats.payments.revenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Total Refunds</span>
              <span className="text-sm font-bold text-rose-600">₹{stats.payments.refunds.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Net Revenue</span>
              <span className="text-sm font-bold text-emerald-600">₹{(stats.payments.revenue - stats.payments.refunds).toFixed(2)}</span>
            </div>
            <div className="pt-3 border-t border-slate-100">
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div 
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((stats.payments.revenue / 500000) * 100, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-slate-400 mt-1">Revenue progress towards ₹5,00,000 goal</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Quick Stats</h3>
            <TrendingUp className="w-5 h-5 text-slate-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-xl">
              <p className="text-2xl font-bold text-blue-600">{stats.applications.pending}</p>
              <p className="text-xs text-blue-600 mt-0.5">Pending Apps</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-xl">
              <p className="text-2xl font-bold text-purple-600">{stats.tenants.active}</p>
              <p className="text-xs text-purple-600 mt-0.5">Active Tenants</p>
            </div>
            <div className="text-center p-3 bg-emerald-50 rounded-xl">
              <p className="text-2xl font-bold text-emerald-600">{stats.users.faculty}</p>
              <p className="text-xs text-emerald-600 mt-0.5">Faculty Members</p>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-xl">
              <p className="text-2xl font-bold text-amber-600">{stats.payments.total}</p>
              <p className="text-xs text-amber-600 mt-0.5">Total Payments</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================
  // QUICK ACTION CARD
  // ============================================
  const QuickActionCard = ({ icon: Icon, title, description, color, onClick }) => (
    <button
      onClick={onClick}
      className="p-4 bg-white rounded-2xl border border-slate-200 hover:border-indigo-400 hover:shadow-lg transition-all group text-left"
    >
      <div className={`p-3 bg-${color}-50 rounded-xl w-fit mb-3 group-hover:scale-105 transition-transform`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="text-xs text-slate-500 mt-0.5">{description}</p>
    </button>
  );

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <p className="mt-4 text-slate-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER PAGE CONTENT
  // ============================================
  const renderPageContent = () => {
    switch (activePage) {
      case 'pricing':
        return (
          <>
            <Header title="💰 Pricing Management" />
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
              <PricingManagement 
                pricingData={pricingData}
                tenantPricingData={tenantPricingData}
                auditLogs={auditLogs}
                loading={pricingLoading}
                onRefresh={fetchPricingData}
              />
            </div>
          </>
        );
      
      case 'payments':
        return (
          <>
            <Header title="💳 Payment Management" />
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
              <PaymentManagement />
            </div>
          </>
        );
      
      default:
        return (
          <>
            <Header title="📊 Dashboard" />
            <DashboardContent />
          </>
        );
    }
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        {renderPageContent()}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;